'use client';

import { useRef, useEffect, useState } from 'react';
import Image from 'next/image';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import SwapTextButton from '@/components/ui/SwapTextButton/SwapTextButton';
import { getCmsButtonLinkProps } from '@/lib/cms-button-target';
import CompactPageHero from '@/components/sections/shared/CompactPageHero';
import CmsImage from '@/components/ui/CmsImage';
import { MEDIA_PLACEHOLDER } from '@/lib/media-placeholder';
import { featuredArticle, gridArticles } from '../media-coverage-articles';
import { getImageUrl, getImageUrlForVariant } from '@/lib/homepage-mappers';
import CmsRichTextHeading from '@/components/ui/CmsRichTextHeading';
import type {
  MediaCoverageBgWithText,
  MediaCoverageButton,
  MediaCoverageItem,
  TransformedMediaCoverageData,
} from '@/types/media-coverage';

interface MediaCoverageViewProps {
  locale: string;
  title: string;
  subtitle: string;
  ctaHeading: string;
  ctaEnquire: string;
  ctaImageAlt: string;
  cmsData?: TransformedMediaCoverageData | null;
}

const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];
const ITEMS_PER_PAGE = 9;

const DEFAULT_TICKER_LABELS = [
  'Tribune India',
  'CareEdge Ratings',
  'Business Standard',
  'BestMediaInfo',
  'Business Today',
  'Wonder Cement Press',
  'Tribune India',
  'CareEdge Ratings',
  'Business Standard',
  'BestMediaInfo',
  'Business Today',
  'Wonder Cement Press',
];

const SELECT_STYLE: React.CSSProperties = {
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23A09A93' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 10px center',
};

function formatPublishDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  const month = MONTH_NAMES[Number(m) - 1];
  const day = Number(d);
  const year = Number(y);
  if (!month || !Number.isFinite(day) || !Number.isFinite(year)) return iso;
  return `${month} ${day}, ${year}`;
}

function buttonHref(
  button: MediaCoverageButton | undefined,
  locale: string,
  fallback: string,
): string {
  if (!button?.buttonUrl) return `/${locale}${fallback}`;
  const url = button.buttonUrl;
  if (/^https?:\/\//.test(url)) return url;
  const trimmed = url.startsWith('/') ? url : `/${url}`;
  return `/${locale}${trimmed}`;
}

function extractYear(dateLabel: string): string {
  if (!dateLabel) return '';
  const parts = dateLabel.trim().split(/\s+/);
  return parts[parts.length - 1] || '';
}

function getPageNumbers(current: number, total: number): (number | '...')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | '...')[] = [];
  const left = Math.max(2, current - 2);
  const right = Math.min(total - 1, current + 2);
  pages.push(1);
  if (left > 2) pages.push('...');
  for (let i = left; i <= right; i++) pages.push(i);
  if (right < total - 1) pages.push('...');
  pages.push(total);
  return pages;
}

type ArticleCard = {
  key: string;
  headline: string;
  source: string;
  dateLabel: string;
  url: string | null;
  imageSrc: string;
  imageAlt: string;
  topic?: string;
};

function runMediaCoverageIntroAnimations(gridEl: HTMLDivElement | null) {
  gsap.from('.anim-rule', {
    scaleX: 0,
    transformOrigin: 'left center',
    duration: 1.1,
    ease: 'expo.out',
    delay: 0.2,
  });

  gsap.from('.anim-h1', {
    y: 60,
    opacity: 0,
    duration: 1,
    ease: 'power4.out',
    delay: 0.3,
  });

  gsap.from('.anim-subtitle', {
    y: 20,
    opacity: 0,
    duration: 0.75,
    ease: 'power3.out',
    delay: 0.55,
  });

  gsap.from('.anim-grid-label', {
    x: -20,
    opacity: 0,
    duration: 0.6,
    ease: 'power3.out',
    immediateRender: false,
    scrollTrigger: {
      trigger: '.anim-grid-label',
      start: 'top bottom',
      once: true,
    },
  });

  if (gridEl) {
    gsap.from(gridEl.querySelectorAll('.mc-card'), {
      y: 40,
      opacity: 0,
      duration: 0.6,
      ease: 'power3.out',
      stagger: 0.07,
      immediateRender: false,
      scrollTrigger: {
        trigger: gridEl,
        start: 'top bottom',
        once: true,
      },
    });
  }

  gsap.from('.mc-cta', {
    y: 36,
    opacity: 0,
    duration: 0.8,
    ease: 'power3.out',
    immediateRender: false,
    scrollTrigger: {
      trigger: '.mc-cta',
      start: 'top bottom',
      once: true,
    },
  });
}

function buildGridData(cmsData: TransformedMediaCoverageData | null | undefined): ArticleCard[] {
  return cmsData?.grid?.length
    ? cmsData.grid.map((item: MediaCoverageItem) => ({
        key: `article-${item.id}`,
        headline: item.mainHeading ?? '',
        source: item.publisher ?? '',
        dateLabel: formatPublishDate(item.pusblishDate),
        url: item.mediaUrl ?? null,
        imageSrc: getImageUrlForVariant(item.mediaImage, 'medium') || '/images/about_img.jpg',
        imageAlt: item.mediaImage?.alternativeText || item.mainHeading || '',
        topic: item.title ?? undefined,
      }))
    : gridArticles.map((a, idx) => ({
        key: `article-${idx + 1}`,
        ...a,
        url: null,
      }));
}

/** Compute filter facets + the filtered/paginated view over the article list. */
function computeArticleView(
  allArticles: ArticleCard[],
  filters: { search: string; yearFilter: string; sourceFilter: string; topicFilter: string },
  page: number,
) {
  const { search, yearFilter, sourceFilter, topicFilter } = filters;

  const uniqueYears = Array.from(
    new Set(allArticles.map((a) => extractYear(a.dateLabel)).filter(Boolean)),
  ).sort((a, b) => Number(b) - Number(a));

  const uniqueSources = Array.from(
    new Set(allArticles.map((a) => a.source).filter(Boolean)),
  ).sort();

  const uniqueTopics = Array.from(
    new Set(allArticles.map((a) => a.topic).filter(Boolean)),
  ).sort() as string[];

  const filtered = allArticles.filter((a) => {
    const q = search.trim().toLowerCase();
    if (q && !a.headline.toLowerCase().includes(q) && !a.source.toLowerCase().includes(q))
      return false;
    if (yearFilter && extractYear(a.dateLabel) !== yearFilter) return false;
    if (sourceFilter && a.source !== sourceFilter) return false;
    if (topicFilter && a.topic !== topicFilter) return false;
    return true;
  });

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const pageItems = filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);
  const activeFilterCount = [search, yearFilter, sourceFilter, topicFilter].filter(Boolean).length;

  return {
    uniqueYears,
    uniqueSources,
    uniqueTopics,
    filtered,
    totalPages,
    pageItems,
    activeFilterCount,
  };
}

function resolveTickerLabels(cmsTickerLabels: string[], uniqueSources: string[]): string[] {
  if (cmsTickerLabels.length > 0) {
    return [...cmsTickerLabels, ...cmsTickerLabels];
  }
  if (uniqueSources.length >= 2) {
    return [...uniqueSources, ...uniqueSources];
  }
  return DEFAULT_TICKER_LABELS;
}

function MediaArticleCard({ article, readLabel }: { article: ArticleCard; readLabel: string }) {
  return (
    <article
      key={article.key}
      className="mc-card group relative flex flex-col overflow-hidden rounded-xl bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
    >
      {article.url && (
        <a
          href={article.url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={article.headline}
          className="absolute inset-0 z-10"
        />
      )}

      <div className="relative aspect-[16/10] shrink-0 overflow-hidden bg-neutral-100">
        <CmsImage
          src={article.imageSrc}
          alt={article.imageAlt}
          fill
          className="object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]"
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
          fallbackSrc={MEDIA_PLACEHOLDER}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/5 to-transparent" />
        <div className="absolute inset-x-0 top-0 h-0.5 origin-left scale-x-0 bg-[#E31E24] transition-transform duration-300 group-hover:scale-x-100" />
        <span className="absolute bottom-3 left-3 inline-flex items-center rounded-full bg-white px-2.5 py-[5px] font-figtree text-[8px] font-bold uppercase leading-none tracking-[0.14em] text-[#1C1917] shadow-sm">
          {article.source}
        </span>
        {article.topic && (
          <span className="absolute right-3 top-3 inline-flex items-center rounded-full bg-[#E31E24] px-2.5 py-[5px] font-figtree text-[8px] font-bold uppercase leading-none tracking-[0.14em] text-white">
            {article.topic}
          </span>
        )}
      </div>

      <div className="flex flex-1 flex-col gap-3 px-5 py-4">
        <h3 className="flex-1 font-figtree text-[0.9375rem] font-semibold leading-[1.45] text-[#1C1917]">
          {article.headline}
        </h3>
        <div className="flex items-center justify-between gap-2 border-t border-[#e4e4e4] pt-3">
          <time className="font-figtree text-[10px] tracking-wide text-[#A09A93]">
            {article.dateLabel}
          </time>
          <span className="translate-x-2 whitespace-nowrap font-figtree text-[9px] font-bold uppercase tracking-[0.14em] text-[#E31E24] opacity-0 transition-all duration-200 group-hover:translate-x-0 group-hover:opacity-100">
            {readLabel} →
          </span>
        </div>
      </div>
    </article>
  );
}

function MediaFilterBar({
  search,
  setSearch,
  yearFilter,
  setYearFilter,
  sourceFilter,
  setSourceFilter,
  topicFilter,
  setTopicFilter,
  uniqueYears,
  uniqueSources,
  uniqueTopics,
  activeFilterCount,
  clearAllFilters,
}: {
  search: string;
  setSearch: React.Dispatch<React.SetStateAction<string>>;
  yearFilter: string;
  setYearFilter: React.Dispatch<React.SetStateAction<string>>;
  sourceFilter: string;
  setSourceFilter: React.Dispatch<React.SetStateAction<string>>;
  topicFilter: string;
  setTopicFilter: React.Dispatch<React.SetStateAction<string>>;
  uniqueYears: string[];
  uniqueSources: string[];
  uniqueTopics: string[];
  activeFilterCount: number;
  clearAllFilters: () => void;
}) {
  return (
    <div className="mb-5 rounded-2xl border border-[#e4e4e4] bg-white px-5 py-4 shadow-sm sm:mb-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
        <div className="relative min-w-[180px] flex-1">
          <svg
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#A09A93]"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          <input
            type="text"
            placeholder="Search articles..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-xl border border-[#e4e4e4] bg-white py-2.5 pl-9 pr-4 font-figtree text-[12px] text-[#1C1917] transition-colors placeholder:text-[#A09A93] focus:border-[#E31E24] focus:outline-none focus:ring-1 focus:ring-[#E31E24]/20"
          />
        </div>

        <div className="hidden h-7 w-px bg-[#e4e4e4] sm:block" />

        <select
          value={yearFilter}
          onChange={(e) => setYearFilter(e.target.value)}
          style={SELECT_STYLE}
          className="min-w-[110px] cursor-pointer appearance-none rounded-xl border border-[#e4e4e4] bg-white px-4 py-2.5 pr-7 font-figtree text-[12px] font-medium text-[#1C1917] transition-colors focus:border-[#E31E24] focus:outline-none focus:ring-1 focus:ring-[#E31E24]/20"
        >
          <option value="">All Years</option>
          {uniqueYears.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>

        <select
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value)}
          style={SELECT_STYLE}
          className="min-w-[150px] cursor-pointer appearance-none rounded-xl border border-[#e4e4e4] bg-white px-4 py-2.5 pr-7 font-figtree text-[12px] font-medium text-[#1C1917] transition-colors focus:border-[#E31E24] focus:outline-none focus:ring-1 focus:ring-[#E31E24]/20"
        >
          <option value="">All Publications</option>
          {uniqueSources.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>

        {uniqueTopics.length > 0 && (
          <select
            value={topicFilter}
            onChange={(e) => setTopicFilter(e.target.value)}
            style={SELECT_STYLE}
            className="min-w-[130px] cursor-pointer appearance-none rounded-xl border border-[#e4e4e4] bg-white px-4 py-2.5 pr-7 font-figtree text-[12px] font-medium text-[#1C1917] transition-colors focus:border-[#E31E24] focus:outline-none focus:ring-1 focus:ring-[#E31E24]/20"
          >
            <option value="">All Topics</option>
            {uniqueTopics.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        )}

        {activeFilterCount > 0 && (
          <button
            onClick={clearAllFilters}
            className="flex items-center gap-1.5 whitespace-nowrap rounded-xl border border-[#E31E24]/20 bg-[#E31E24]/5 px-3 py-2 font-figtree text-[11px] font-semibold text-[#E31E24] transition-colors hover:bg-[#E31E24]/10"
          >
            <svg
              width="10"
              height="10"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
            Clear ({activeFilterCount})
          </button>
        )}
      </div>
    </div>
  );
}

function MediaPagination({
  page,
  totalPages,
  setPage,
}: {
  page: number;
  totalPages: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
}) {
  const safeTotal = Math.max(1, totalPages);
  return (
    <>
      <div className="mt-8 flex items-center justify-center gap-1.5">
        <button
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page === 1}
          aria-label="Previous page"
          className="flex h-9 w-9 items-center justify-center rounded-xl border border-[#e4e4e4] bg-white text-[#1C1917] transition-all duration-200 hover:border-[#E31E24] hover:text-[#E31E24] disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:border-[#e4e4e4] disabled:hover:text-[#1C1917]"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>

        {getPageNumbers(page, safeTotal).map((num, i) =>
          num === '...' ? (
            <span
              key={i}
              className="flex h-9 w-9 items-center justify-center font-figtree text-[12px] text-[#A09A93]"
            >
              …
            </span>
          ) : (
            <button
              key={num}
              onClick={() => setPage(num)}
              aria-label={`Page ${num}`}
              aria-current={page === num ? 'page' : undefined}
              className={`flex h-9 w-9 items-center justify-center rounded-xl border font-figtree text-[12px] font-semibold transition-all duration-200 ${
                page === num
                  ? 'border-[#E31E24] bg-[#E31E24] text-white shadow-sm'
                  : 'border-[#e4e4e4] bg-white text-[#1C1917] hover:border-[#E31E24] hover:text-[#E31E24]'
              }`}
            >
              {num}
            </button>
          ),
        )}

        <button
          onClick={() => setPage((p) => Math.min(safeTotal, p + 1))}
          disabled={page === safeTotal}
          aria-label="Next page"
          className="flex h-9 w-9 items-center justify-center rounded-xl border border-[#e4e4e4] bg-white text-[#1C1917] transition-all duration-200 hover:border-[#E31E24] hover:text-[#E31E24] disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:border-[#e4e4e4] disabled:hover:text-[#1C1917]"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>
      </div>

      <p className="mt-3 text-center font-figtree text-[11px] text-[#A09A93]">
        Page {page} of {safeTotal}
      </p>
    </>
  );
}

export default function MediaCoverageView({
  locale,
  title,
  subtitle,
  ctaHeading,
  ctaEnquire,
  ctaImageAlt,
  cmsData,
}: MediaCoverageViewProps) {
  const mainRef = useRef<HTMLElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  const [search, setSearch] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [topicFilter, setTopicFilter] = useState('');
  const [page, setPage] = useState(1);

  // Prefer Strapi large rendition (same as video-gallery CTA); local file only if CMS image is missing.
  const ctaImageUrl =
    getImageUrlForVariant(cmsData?.cta?.image, 'large') ||
    getImageUrl(cmsData?.cta?.image) ||
    '/images/media_cs.png';
  const [ctaSrc, setCtaSrc] = useState(ctaImageUrl);

  useEffect(() => {
    setCtaSrc(ctaImageUrl);
  }, [ctaImageUrl]);

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);

    const ctx = gsap.context(() => {
      runMediaCoverageIntroAnimations(gridRef.current);
    }, mainRef);

    return () => ctx.revert();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [search, yearFilter, sourceFilter, topicFilter]);

  const displayTitle = cmsData?.pageTitle || title;
  const subHeadingHtml = cmsData?.subHeadingHtml;
  const readLabel = cmsData?.cursorText?.trim() || 'Read';

  const featuredData: ArticleCard = cmsData?.featured
    ? {
        key: `article-${cmsData.featured.id}`,
        headline: cmsData.featured.mainHeading ?? featuredArticle.headline,
        source: cmsData.featured.publisher ?? featuredArticle.source,
        dateLabel: formatPublishDate(cmsData.featured.pusblishDate) || featuredArticle.dateLabel,
        imageSrc:
          getImageUrlForVariant(cmsData.featured.mediaImage, 'large') || featuredArticle.imageSrc,
        imageAlt: cmsData.featured.mediaImage?.alternativeText || featuredArticle.imageAlt,
        url: cmsData.featured.mediaUrl ?? null,
        topic: cmsData.featured.title ?? featuredArticle.topic,
      }
    : { ...featuredArticle, key: 'article-0', url: null };

  const gridData: ArticleCard[] = buildGridData(cmsData);

  const allArticles: ArticleCard[] = [featuredData, ...gridData];

  const {
    uniqueYears,
    uniqueSources,
    uniqueTopics,
    filtered,
    totalPages,
    pageItems,
    activeFilterCount,
  } = computeArticleView(allArticles, { search, yearFilter, sourceFilter, topicFilter }, page);

  const cmsTickerLabels = (cmsData?.ticker ?? [])
    .map((item) => item.heading?.trim())
    .filter((label): label is string => Boolean(label));

  const tickerLabels = resolveTickerLabels(cmsTickerLabels, uniqueSources);

  function clearAllFilters() {
    setSearch('');
    setYearFilter('');
    setSourceFilter('');
    setTopicFilter('');
  }

  const cta: MediaCoverageBgWithText | null = cmsData?.cta ?? null;
  const ctaPrimary = cta?.buttonWhite?.[0];
  const ctaBg = cta?.bgColor || '#E31E24';
  const ctaImageAltResolved = cta?.image?.alternativeText || ctaImageAlt;
  const ctaHeadingResolved = cta?.heading || ctaHeading;
  const ctaButtonText = ctaPrimary?.buttonText || ctaEnquire;
  const ctaButtonHref = buttonHref(ctaPrimary, locale, '/contact');

  return (
    <main ref={mainRef} className="font-figtree text-[#1C1917] antialiased">
      <style>{`
        @keyframes mc-ticker {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .mc-ticker-track {
          animation: mc-ticker 30s linear infinite;
          will-change: transform;
        }
        .mc-ticker-track:hover { animation-play-state: paused; }
      `}</style>

      <CompactPageHero>
        {cmsData?.bannerSmallHeading?.length ? (
          <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-2 sm:mb-5">
            {cmsData.bannerSmallHeading.map((part) => (
              <span
                key={part.id}
                className="shrink-0 font-figtree text-[9px] font-bold uppercase tracking-[0.32em]"
                style={{ color: part.textColor ?? undefined }}
              >
                {part.text}
              </span>
            ))}
          </div>
        ) : null}
        <CmsRichTextHeading
          className="anim-h1 hero-title text-white"
          html={cmsData?.titleRichText}
          fallback={displayTitle}
        />
        <div className="mt-3 max-w-2xl sm:mt-4">
          {subHeadingHtml ? (
            <div
              className="anim-subtitle hero-subtitle text-white/60 [&_p]:m-0"
              dangerouslySetInnerHTML={{ __html: subHeadingHtml }}
            />
          ) : (
            <p className="anim-subtitle hero-subtitle text-white/60">{subtitle}</p>
          )}
        </div>
      </CompactPageHero>

      <div className="relative z-[1] overflow-hidden bg-[#1C1917] py-3.5 select-none">
        <div className="mc-ticker-track flex items-center whitespace-nowrap">
          {tickerLabels.map((label, i) => (
            <span key={i} className="inline-flex items-center gap-5 px-5">
              <span className="font-figtree text-[10px] font-bold uppercase tracking-[0.22em] text-white/60">
                {label}
              </span>
              <span className="h-1 w-1 shrink-0 rounded-full bg-[#E31E24]" />
            </span>
          ))}
        </div>
      </div>

      <div className="bg-[#f3f3f3]">
        <div className="mx-auto container max-sm:px-4 px-4 py-10 sm:py-12 md:py-14">
          <MediaFilterBar
            search={search}
            setSearch={setSearch}
            yearFilter={yearFilter}
            setYearFilter={setYearFilter}
            sourceFilter={sourceFilter}
            setSourceFilter={setSourceFilter}
            topicFilter={topicFilter}
            setTopicFilter={setTopicFilter}
            uniqueYears={uniqueYears}
            uniqueSources={uniqueSources}
            uniqueTopics={uniqueTopics}
            activeFilterCount={activeFilterCount}
            clearAllFilters={clearAllFilters}
          />

          <div className="anim-grid-label mb-5 flex items-center justify-between sm:mb-6">
            <span className="font-figtree text-[9px] font-bold uppercase tracking-[0.28em] text-[#A09A93]">
              Coverage
            </span>
            <span className="font-figtree text-[9px] font-medium text-[#A09A93]">
              {activeFilterCount > 0
                ? `${filtered.length} of ${allArticles.length} stories`
                : `${allArticles.length} stories`}
            </span>
          </div>

          {pageItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-4 py-12 text-center sm:py-14">
              <div className="flex h-12 w-12 items-center justify-center rounded-full border border-[#e4e4e4] bg-[#f3f3f3]">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#A09A93"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="11" cy="11" r="8" />
                  <path d="M21 21l-4.35-4.35" />
                </svg>
              </div>
              <p className="font-figtree text-[0.9375rem] font-semibold text-[#1C1917]">
                No articles match your filters
              </p>
              <button
                onClick={clearAllFilters}
                className="font-figtree text-[12px] font-medium text-[#E31E24] underline underline-offset-2"
              >
                Clear all filters
              </button>
            </div>
          ) : (
            <div
              ref={gridRef}
              className="grid grid-cols-1 gap-x-5 gap-y-6 sm:grid-cols-2 lg:grid-cols-3"
            >
              {pageItems.map((article) => (
                <MediaArticleCard key={article.key} article={article} readLabel={readLabel} />
              ))}
            </div>
          )}

          <MediaPagination page={page} totalPages={totalPages} setPage={setPage} />
        </div>

        <div className="mx-auto container max-sm:px-4 px-4 pb-10 sm:pb-12 md:pb-14">
          <section
            className={`${cmsData && !cmsData.cta ? 'hidden' : ''} mc-cta relative overflow-hidden rounded-2xl`}
            style={{ backgroundColor: ctaBg }}
            aria-labelledby="mc-cta-heading"
          >
            <div className="flex flex-col md:min-h-[220px] md:flex-row md:items-stretch lg:min-h-[240px]">
              <div className="flex flex-1 flex-col justify-center gap-5 px-6 py-7 sm:px-8 sm:py-8 md:px-10 md:py-10 lg:px-12 lg:py-12">
                <h2
                  id="mc-cta-heading"
                  className="max-w-xl text-lg font-bold leading-tight text-white sm:text-2xl md:text-[1.75rem]"
                >
                  {ctaHeadingResolved}
                </h2>
                <div>
                  <SwapTextButton
                    tone="light"
                    text={ctaButtonText}
                    href={ctaButtonHref}
                    {...getCmsButtonLinkProps(ctaPrimary)}
                    showArrow
                    className="shadow-md"
                    aria-label={ctaPrimary?.ariaLabel || ctaButtonText}
                  />
                </div>
              </div>
              <div className="relative min-h-[190px] w-full shrink-0 sm:min-h-[210px] md:min-h-0 md:w-[38%]">
                {/* fill/absolute ignores parent padding — inset the image box from the right */}
                <div className="absolute inset-y-0 left-0 right-5 sm:right-7 md:right-8 lg:right-10">
                  <Image
                    src={ctaSrc}
                    alt={ctaImageAltResolved}
                    fill
                    priority
                    sizes="(max-width: 768px) 240px, 38vw"
                    className="object-contain object-right-bottom"
                    onError={() => setCtaSrc('/images/media_cs.png')}
                  />
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </main>
  );
}
